<template>
    <div id="config-rights">
        <FilterView placeholder="Chercher un droit de validation..." v-model="searchTerm">
            <CustomButton
                v-if="selected.length"
                icon="trash-alt"
                class="default red-theme"
                @click="showConfirmMany = true"
            >
                Supprimer
            </CustomButton>
            <CustomButton icon="plus" class="default green-theme" @click="handleNew">
                Ajouter un droit de validation
            </CustomButton>
        </FilterView>
        <Modal
            v-if="showEdit"
            v-model="right"
            :business="business"
            :objects="objects"
            :keys="keys"
            @dismiss="showEdit = false"
            @close="handleClose"
        />
        <Confirm v-if="showConfirmOne" @dismiss="showConfirmOne = false" @close="handleDeleteOne">
            <span slot="header">Supprimer le droit de validation</span>
            <span slot="body">Êtes-vous sûr de vouloir supprimer ce droit de validation ?</span>
        </Confirm>
        <Confirm
            v-if="showConfirmMany"
            @dismiss="showConfirmMany = false"
            @close="handleDeleteMany"
        >
            <span slot="header">
                Supprimer {{ selected.length }} {{ selected.length > 1 ? 'droits' : 'droit' }} de
                validation
            </span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer les droits de validation sélectionnés ?
            </span>
        </Confirm>
        <h2 class="title">Droits de validation</h2>
        <LoadingDots v-if="isLoading" />
        <CardPager>
            <RightCard
                v-for="item in filtered"
                :item="item"
                :key="item.id"
                :handleEdit="handleEdit"
                :handleConfirm="handleConfirm"
                :selected="selected"
                @selected="handleSelect"
            />
        </CardPager>
    </div>
</template>

<script>
import FilterView from '@/components/config/Filter';
import Modal from '@/components/config/ModalRight';
import Confirm from '@/components/config/DeleteConfirm';
import CustomButton from '@/components/IconButton';
import LoadingDots from '@/components/LoadingDots';
import RightCard from '@/components/config/RightCard';
import CardPager from '@/components/CardPager';

export default {
    data() {
        return {
            showEdit: false,
            showConfirmOne: false,
            showConfirmMany: false,
            rights: [],
            business: [],
            objects: [],
            keys: [],
            right: null,
            selected: [],
            searchTerm: null,
            isLoading: true,
        };
    },
    watch: {
        searchTerm() {
            this.selected.splice(0);
        },
    },
    computed: {
        filtered() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.rights.filter(
                    (r) => re.test(r.business.name) || re.test(r.object.name),
                );
            }
            return this.rights;
        },
    },
    mounted() {
        this.isLoading = true;
        Promise.all([
            this.$http.get('/api/right'),
            this.$http.get('/api/business-group'),
            this.$http.get('/api/object-group'),
            this.$http.get('/api/tag'),
        ]).then(
            (responses) => {
                // success callback
                this.rights = responses[0].body;
                this.business = responses[1].body.map((b) => ({
                    id: b.id,
                    text: b.name,
                }));
                this.objects = responses[2].body.map((o) => ({
                    id: o.id,
                    text: o.name,
                }));

                // tags avec traduction
                this.keys = responses[3].body
                    .filter((t) => t.name_fr != '' && t.name_fr != null)
                    .map((t) => ({ id: t.id, text: `${t.name} (${t.name_fr})` }));
                // tags sans traduction
                this.keysBis = responses[3].body
                    .filter((t) => t.name_fr == '' || t.name_fr == null)
                    .map((t) => ({ id: t.id, text: `${t.name}` }));
                // fusion des deux arrays
                this.keys.push(...this.keysBis);
                // tri du array final
                Array.prototype.sortOn = function(key) {
                    this.sort(function(a, b) {
                        if (a[key].toLowerCase() < b[key].toLowerCase()) {
                            return -1;
                        } else if (a[key].toLowerCase() > b[key].toLowerCase()) {
                            return 1;
                        }
                        return 0;
                    });
                };
                this.keys.sortOn('text');

                this.isLoading = false;
            },
            (responses) => {
                // error callback
                console.log(JSON.stringify(responses));
                this.isLoading = false;
            },
        );
    },
    methods: {
        _getRight(id) {
            const right = this.rights.find((r) => r.id === id);
            return {
                ...right,
                business: {
                    ...right.business,
                    text: right.business.name,
                },
                object: {
                    ...right.object,
                    text: right.object.name,
                },
                keys: right.keys.map((k) => ({
                    id: k.id,
                    text: `${k.name} (${k.name_fr})`,
                })),
            };
        },
        loadRights() {
            this.isLoading = true;
            this.$http
                .get('/api/right')
                .then(
                    (response) => {
                        // success callback
                        this.rights = response.body;
                    },
                    (response) => {
                        // error callback
                        console.log(JSON.stringify(response));
                    },
                )
                .finally(() => (this.isLoading = false));
        },
        handleSelect(event) {
            const { id, checked } = event;
            const index = this.selected.findIndex((s) => s === id);
            if (checked) {
                if (index === -1) {
                    this.selected.push(id);
                }
            } else {
                if (index > -1) {
                    this.selected.splice(index, 1);
                }
            }
        },
        handleConfirm(item) {
            this.right = this._getRight(item);
            this.showConfirmOne = true;
        },
        handleEdit(item) {
            this.right = this._getRight(item);
            this.showEdit = true;
        },
        handleClose() {
            // Crée le nouveau droit, si groupe métier et groupe d'objets renseignés
            if (!this.right.id && this.right.business.id && this.right.object.id) {
                this.$http
                    .post('/api/right', this.right)
                    .then(() => {
                        this.loadRights();
                        this.showEdit = false;
                        this.right = null;
                    })
                    .catch();
                // Met à jour le droit, si groupe métier et groupe d'objets renseignés
            } else if (this.right.id && this.right.business.id && this.right.object.id) {
                this.$http
                    .put(`/api/right/${this.right.id}`, this.right)
                    .then(() => {
                        this.loadRights();
                        this.showEdit = false;
                        this.right = null;
                    })
                    .catch();
            }
        },
        handleNew() {
            this.right = {
                id: null,
                business: {},
                object: {},
                keys: [],
            };
            this.showEdit = true;
        },
        handleDeleteOne() {
            this.$http
                .delete(`/api/right/${this.right.id}`)
                .then(() => {
                    const index = this.rights.findIndex((r) => r.id === this.right.id);
                    this.rights.splice(index, 1);
                    this.showConfirmOne = false;
                })
                .catch();
        },
        handleDeleteMany() {
            const requests = this.selected.map((s) => this.$http.delete(`/api/right/${s}`));
            Promise.all(requests)
                .then(() => {
                    this.showConfirmMany = false;
                    this.selected.splice(0);
                    this.isLoading = true;
                    return this.$http.get('/api/right');
                })
                .then((response) => {
                    // success callback
                    this.rights = response.body;
                    this.isLoading = false;
                })
                .catch(() => {
                    this.isLoading = false;
                });
        },
    },
    components: {
        Modal,
        Confirm,
        FilterView,
        CustomButton,
        LoadingDots,
        RightCard,
        CardPager,
    },
};
</script>

<style lang="scss"></style>
